import React, { useEffect, useRef, useState } from "react";
import { Lightbulb, Target, TrendingUp, Users } from "lucide-react";

// Steps data
const steps = [
  {
    number: "01",
    icon: Lightbulb,
    title: "Problem & Market",
    description: "Identify the market gap, target audience, and validated pain points.",
    color: "bg-lime-400",
  },
  {
    number: "02",
    icon: Target,
    title: "Product Overview",
    description: "Showcase your product differentiation and strong value proposition.",
    color: "bg-teal-400",
  },
  {
    number: "03",
    icon: TrendingUp,
    title: "Traction & Metrics",
    description: "Share growth metrics, KPIs, revenue traction, and market validation.",
    color: "bg-cyan-400",
  },
  {
    number: "04",
    icon: Users,
    title: "Team & Ask",
    description: "Highlight your team's strengths and clearly articulate your funding.",
    color: "bg-amber-400",
  },
];

// Hook: animate when visible
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );

    if (ref.current) obs.observe(ref.current);

    return () => obs.disconnect();
  }, []);

  return { ref, visible };
};

export default function PitchExpectations() {
  const { ref, visible } = useReveal();

  return (
    <section className="py-6 px-6 bg-secondary text-textPrimary overflow-hidden">
      <div className="max-w-6xl mx-auto" ref={ref}>
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
            What Investors Expect in Your Pitch Deck
          </h2>
          <p className="text-textMuted text-sm sm:text-lg">
            A clear, structured pitch that covers these fundamentals
          </p>
        </div>

        {/* White Card Container */}
        <div className="bg-primary/40 backdrop-blur-xl border border-[#d4af37]/20 rounded-3xl p-4 sm:p-8 md:p-12 shadow-[0_0_40px_rgba(212,175,55,0.15)] relative">
          <h3 className="text-center text-xl sm:text-2xl md:text-3xl font-semibold text-[#eae3cb] mb-8 md:mb-16">
            Circular Process
          </h3>

          {/* Curved Line — Positioned behind icons */}
          <div className="relative mb-20 hidden md:block">
            <svg
              width="100%"
              height="200"
              viewBox="0 0 800 200"
              className={`absolute left-1/2 -translate-x-1/2 transition-all duration-[2.2s] -z-10 ${
                visible ? "draw-line" : "opacity-0"
              }`}
            >
              <path
                d="M 50,100 C 200,40 200,160 400,100 C 600,40 600,160 750,100"
                stroke="#D4AF37"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={index}
                  className={`
                    flex flex-col items-center text-center space-y-6
                    transition-all duration-500
                    hover:-translate-y-4
                    rounded-xl p-3
                    relative z-20
                  `}
                >
                  {/* Number + Icon */}
                  <div className="flex flex-col items-center space-y-3">
                    <span className="text-xl font-bold text-[#D4AF37] opacity-90">
                      {step.number}
                    </span>

                    {/* Floating Icon Bubble */}
                    <div
                      className={`
                        relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/10 border border-[#D4AF37]/30 shadow-xl
                        flex items-center justify-center
                        transition-all duration-500
                        hover:shadow-[0_0_20px_rgba(212,175,55,0.7)]
                        floating-icon
                      `}
                    >
                      <div
                        className={`w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center ${step.color} bg-opacity-30 backdrop-blur-sm`}
                      >
                        <Icon className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white drop-shadow-[0_0_5px_rgba(212,175,55,0.8)]" />
                      </div>

                      {/* Ring Glow */}
                      <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37] opacity-0 hover:opacity-100 transition-all duration-500 blur-[2px]" />
                    </div>
                  </div>

                  {/* Title */}
                  <h4 className="text-lg font-semibold text-white uppercase tracking-wide">
                    {step.title}
                  </h4>

                  {/* Description */}
                  <p className="text-sm text-textMuted leading-relaxed px-4 transition-all duration-300 group-hover:text-white">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        /* Draw animation */
        .draw-line path {
          stroke-dasharray: 1400;
          stroke-dashoffset: 1400;
          animation: draw 2.2s ease forwards;
        }

        @keyframes draw {
          to {
            stroke-dashoffset: 0;
          }
        }

        /* Floating icon animation */
        .floating-icon {
          animation: float 4s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </section>
  );
}